import React, { useEffect, useRef } from 'react';
import { Box, Theme } from '@mui/material';

import focusRing from '../../theme/focus';
import { RESIZE_CURSORS } from '../../hooks/useResizablePanel';
import { MIN_PANEL_WIDTH } from './dockedPanelWidth';
import { useDockContext } from './PinguDockContext';

const EDGE_WIDTH = 6;
const KEYBOARD_STEP = 20;
const LARGE_KEYBOARD_STEP = 100;

const clearDragStyles = () => {
  document.body.style.cursor = '';
  document.body.style.userSelect = '';
};

// The docked panel's left edge. Dragging it, or the arrow keys once it has focus, set
// the chosen width, and the page beside it reflows in the same render.
const PinguDockedResizeEdge = () => {
  const { dockedWidth, dockedMaxWidth, setDockedWidth } = useDockContext();
  const dragStart = useRef<{ x: number; width: number } | null>(null);

  // A drag still live when the panel floats or closes would leave the cursor behind.
  useEffect(() => clearDragStyles, []);

  const startDrag = (event: React.PointerEvent<HTMLDivElement>) => {
    // Only the primary button resizes. A right click's context menu can swallow the
    // pointerup and leave the drag cursor and user-select behind.
    if (event.button !== 0) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    dragStart.current = { x: event.clientX, width: dockedWidth };
    document.body.style.cursor = RESIZE_CURSORS.w;
    document.body.style.userSelect = 'none';
  };

  const drag = (event: React.PointerEvent<HTMLDivElement>) => {
    const start = dragStart.current;
    // Moving the edge left widens the panel.
    if (start) setDockedWidth(start.width + start.x - event.clientX);
  };

  const endDrag = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    dragStart.current = null;
    clearDragStyles();
  };

  const resizeWithKeyboard = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const step = event.shiftKey ? LARGE_KEYBOARD_STEP : KEYBOARD_STEP;
    const deltas: Record<string, number> = { ArrowLeft: step, ArrowRight: -step };
    const delta = deltas[event.key];
    if (delta === undefined) return;
    event.preventDefault();
    setDockedWidth(dockedWidth + delta);
  };

  return (
    <Box
      role="separator"
      tabIndex={0}
      aria-label="Resize Pingwin panel"
      aria-orientation="vertical"
      aria-valuemin={MIN_PANEL_WIDTH}
      aria-valuemax={dockedMaxWidth}
      aria-valuenow={dockedWidth}
      onPointerDown={startDrag}
      onPointerMove={drag}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onKeyDown={resizeWithKeyboard}
      sx={{
        // Outside the panel, which clips its content, so the focus ring shows in full.
        position: 'fixed',
        top: 0,
        bottom: 0,
        right: dockedWidth - EDGE_WIDTH / 2,
        width: EDGE_WIDTH,
        zIndex: (theme: Theme) => theme.zIndex.pinguDockedPanel,
        cursor: RESIZE_CURSORS.w,
        touchAction: 'none',
        '&:focus-visible': { outline: 'none', boxShadow: focusRing },
      }}
    />
  );
};

export default PinguDockedResizeEdge;
